// src/pages/Generate.js
import React, { useState, useCallback } from "react";
import { useToast } from "../components/Toast";
import AdSlot from "../components/AdSlot";

const STYLES = [
  { key: "realistic",   label: "📷 Realistic",    prompt: "photorealistic, 8k, detailed, professional photography" },
  { key: "anime",       label: "🎌 Anime",         prompt: "anime style, vibrant colors, detailed illustration" },
  { key: "art",         label: "🎨 Digital Art",   prompt: "digital art, concept art, artstation, detailed" },
  { key: "3d",          label: "🧊 3D Render",     prompt: "3d render, octane render, cinematic lighting, 4k" },
  { key: "watercolor",  label: "🖌 Watercolor",    prompt: "watercolor painting, soft colors, artistic" },
  { key: "minimal",     label: "⬜ Minimal",        prompt: "minimalist, clean, simple, white background" },
  { key: "fantasy",     label: "🧙 Fantasy",        prompt: "fantasy art, epic, magical, detailed illustration" },
  { key: "cyberpunk",   label: "🤖 Cyberpunk",     prompt: "cyberpunk, neon lights, futuristic, dark atmosphere" },
];

const SIZES = [
  { key: "1024x1024", label: "1:1 Square",   w: 1024, h: 1024 },
  { key: "1280x720",  label: "16:9 Wide",    w: 1280, h: 720  },
  { key: "720x1280",  label: "9:16 Portrait", w: 720,  h: 1280 },
  { key: "1024x768",  label: "4:3 Classic",  w: 1024, h: 768  },
];

const EXAMPLES = [
  "A majestic lion on a mountain at sunset",
  "Futuristic city with flying cars at night",
  "Beautiful cherry blossom forest in spring",
  "Underwater city with bioluminescent creatures",
  "Snow-covered cabin in a pine forest",
  "Abstract galaxy with colorful nebulas",
  "Ancient temple hidden in a jungle",
  "Cozy coffee shop on a rainy day",
];

const DownloadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="7 10 12 15 17 10"/>
    <line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);

const Generate = () => {
  const { success, error: toastError } = useToast();

  const [prompt,      setPrompt]      = useState("");
  const [style,       setStyle]       = useState("realistic");
  const [size,        setSize]        = useState("1024x1024");
  const [generating,  setGenerating]  = useState(false);
  const [imageUrl,    setImageUrl]    = useState(null);
  const [history,     setHistory]     = useState([]);
  const [imgLoaded,   setImgLoaded]   = useState(false);

  const selectedSize   = SIZES.find(s => s.key === size);
  const selectedStyle  = STYLES.find(s => s.key === style);

  const buildUrl = useCallback((p, st, sz) => {
    const fullPrompt = `${p}, ${st.prompt}`;
    const encoded    = encodeURIComponent(fullPrompt);
    return `https://image.pollinations.ai/prompt/${encoded}?width=${sz.w}&height=${sz.h}&nologo=true&enhance=true`;
  }, []);

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toastError("Please enter a prompt first!");
      return;
    }
    setGenerating(true);
    setImageUrl(null);
    setImgLoaded(false);

    const sz  = SIZES.find(s => s.key === size);
    const st  = STYLES.find(s => s.key === style);
    const url = buildUrl(prompt.trim(), st, sz);

    // Pre-load image
    const img = new Image();
    img.onload = () => {
      setImageUrl(url);
      setImgLoaded(true);
      setGenerating(false);
      setHistory(prev => [
        { url, prompt: prompt.trim(), style, size, ts: Date.now() },
        ...prev.slice(0, 11),
      ]);
      success("Image generated!");
    };
    img.onerror = () => {
      setGenerating(false);
      toastError("Generation failed. Please try again.");
    };
    img.src = url;
  };

  const handleDownload = async () => {
    if (!imageUrl) return;
    try {
      const res  = await fetch(imageUrl);
      const blob = await res.blob();
      const burl = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = burl;
      a.download = `halro-ai-${Date.now()}.jpg`;
      a.click();
      URL.revokeObjectURL(burl);
      success("Image downloaded!");
    } catch {
      toastError("Download failed. Please try again.");
    }
  };

  const handleExample = (ex) => {
    setPrompt(ex);
  };

  const handleHistoryClick = (item) => {
    setPrompt(item.prompt);
    setStyle(item.style);
    setSize(item.size);
    setImageUrl(item.url);
    setImgLoaded(true);
  };

  return (
    <div className="page">
      <div className="container gen-page">

        {/* Header */}
        <div className="gen-header">
          <div className="hero__badge">Powered by Pollinations AI · Free & Unlimited</div>
          <h1 className="gen-title">
            AI Image <span className="hero__accent">Generator</span>
          </h1>
          <p className="gen-sub">
            Type anything and watch AI turn it into stunning artwork in seconds.
          </p>
        </div>

        {/* AD SLOT: banner */}
        <AdSlot type="banner" />

        <div className="gen-layout">
          {/* Left — Controls */}
          <div className="gen-controls">

            {/* Prompt */}
            <div className="gen-section">
              <label className="gen-label">Your Prompt</label>
              <textarea
                className="gen-textarea"
                placeholder="Describe the image you want to create..."
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={4}
                maxLength={500}
              />
              <div className="gen-char-count">{prompt.length}/500</div>
            </div>

            {/* Examples */}
            <div className="gen-section">
              <label className="gen-label">✨ Try an example</label>
              <div className="gen-examples">
                {EXAMPLES.map(ex => (
                  <button
                    key={ex}
                    className="gen-example-chip"
                    onClick={() => handleExample(ex)}
                  >
                    {ex}
                  </button>
                ))}
              </div>
            </div>

            {/* Style */}
            <div className="gen-section">
              <label className="gen-label">Style</label>
              <div className="gen-styles">
                {STYLES.map(s => (
                  <button
                    key={s.key}
                    className={`gen-style-btn${style === s.key ? " gen-style-btn--active" : ""}`}
                    onClick={() => setStyle(s.key)}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Size */}
            <div className="gen-section">
              <label className="gen-label">Size</label>
              <div className="gen-sizes">
                {SIZES.map(s => (
                  <button
                    key={s.key}
                    className={`gen-size-btn${size === s.key ? " gen-size-btn--active" : ""}`}
                    onClick={() => setSize(s.key)}
                  >
                    <span className="gen-size-label">{s.label}</span>
                    <span className="gen-size-dim">{s.w}×{s.h}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Generate button */}
            <button
              className="gen-btn"
              onClick={handleGenerate}
              disabled={generating || !prompt.trim()}
            >
              {generating ? (
                <>
                  <span className="spinner" />
                  Generating... (5-15 sec)
                </>
              ) : (
                <>
                  ✦ Generate Image
                </>
              )}
            </button>
          </div>

          {/* Right — Preview */}
          <div className="gen-preview">
            <div className="gen-canvas">
              {generating && (
                <div className="gen-loading">
                  <div className="gen-loading-spinner" />
                  <p className="gen-loading-text">Creating your image…</p>
                  <p className="gen-loading-sub">This takes 5–15 seconds</p>
                </div>
              )}

              {!generating && !imageUrl && (
                <div className="gen-placeholder">
                  <span className="gen-placeholder-icon">🎨</span>
                  <p className="gen-placeholder-text">Your image will appear here</p>
                  <p className="gen-placeholder-sub">Enter a prompt and click Generate</p>
                </div>
              )}

              {imageUrl && imgLoaded && (
                <img
                  src={imageUrl}
                  alt={prompt}
                  className="gen-image"
                />
              )}
            </div>

            {/* AD SLOT: pre-download */}
            {imageUrl && <AdSlot type="pre-download" />}

            {/* Download */}
            {imageUrl && imgLoaded && (
              <div className="gen-actions">
                <button className="gen-download-btn" onClick={handleDownload}>
                  <DownloadIcon />
                  Download Image
                </button>
                <button className="gen-again-btn" onClick={() => { setImageUrl(null); setImgLoaded(false); }}>
                  ✦ Generate Again
                </button>
              </div>
            )}

            {/* Prompt display */}
            {imageUrl && (
              <div className="gen-prompt-display">
                <span className="gen-prompt-label">Prompt:</span>
                <span className="gen-prompt-text">{prompt}</span>
                <span className="badge">{selectedStyle?.label}</span>
                <span className="badge">{selectedSize?.label}</span>
              </div>
            )}
          </div>
        </div>

        {/* History */}
        {history.length > 0 && (
          <div className="gen-history">
            <h2 className="gen-history-title">Recent Generations</h2>
            <div className="gen-history-grid">
              {history.map((item) => (
                <div
                  key={item.ts}
                  className="gen-history-card"
                  onClick={() => handleHistoryClick(item)}
                  title={item.prompt}
                >
                  <img src={item.url} alt={item.prompt} loading="lazy" />
                  <div className="gen-history-overlay">
                    <p className="gen-history-prompt">{item.prompt}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer ad */}
        <AdSlot type="footer" />
      </div>
    </div>
  );
};

export default Generate;
